import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, PriceInput, Select, Textarea } from '@artosera/ui';
import {
  brouillonRegistrePoliceSchema,
  mentionsManquantes,
  REGLES_REGISTRE_POLICE,
  DEVISES,
  MODES_REGLEMENT,
  type BrouillonRegistrePolice,
} from '@artosera/shared';
import { useMutationRegistre, registrePoliceApi } from '../../api/registrePolice.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import styles from './RegistrePolice.module.css';

const champsObjet = [
  'nature',
  'artiste',
  'titre',
  'technique',
  'dimensions',
  'signesDistinctifs',
  'numeroInventaire',
] as const;
const champsVendeur = ['nom', 'prenom', 'raisonSociale', 'siren'] as const;
const champsAdresse = ['ligne1', 'ligne2', 'codePostal', 'ville', 'pays'] as const;
const champsPiece = ['nature', 'numero', 'delivreeLe', 'autorite'] as const;
const chemins = [
  'dateAcquisition',
  ...champsObjet.map((cle) => `objet.${cle}`),
  ...champsVendeur.map((cle) => `vendeur.${cle}`),
  ...champsAdresse.map((cle) => `vendeur.adresse.${cle}`),
  ...champsPiece.map((cle) => `piece.${cle}`),
];
function lire(objet: unknown, chemin: string): unknown {
  return chemin
    .split('.')
    .reduce<unknown>(
      (valeur, cle) =>
        valeur && typeof valeur === 'object' ? (valeur as Record<string, unknown>)[cle] : undefined,
      objet,
    );
}
export function MentionsForm({
  initial,
  galerieSlug,
  voitPiece,
  enCours,
  raisonRequise = false,
  peutValider = false,
  onEnregistrer,
}: {
  initial: unknown;
  galerieSlug: string;
  voitPiece: boolean;
  enCours: boolean;
  raisonRequise?: boolean;
  peutValider?: boolean;
  onEnregistrer: (
    brouillon: BrouillonRegistrePolice,
    raison: string,
    valider: boolean,
  ) => Promise<void>;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [origine] = useState(() => brouillonRegistrePoliceSchema.parse(initial));
  const [valeurs, setValeurs] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      chemins.map((chemin) => {
        const valeur = lire(origine, chemin);
        return [
          chemin,
          valeur instanceof Date ? valeur.toISOString().slice(0, 10) : String(valeur ?? ''),
        ];
      }),
    ),
  );
  const [mode, setMode] = useState(origine.modeAcquisition ?? 'achat');
  const [qualite, setQualite] = useState(origine.vendeur?.qualite ?? 'particulier');
  const [devise, setDevise] = useState(origine.devise ?? 'EUR');
  const [reglement, setReglement] = useState<BrouillonRegistrePolice['modeReglement'] | ''>(
    origine.modeReglement ?? '',
  );
  const [prix, setPrix] = useState<number | null>(origine.prixAchatCentimes ?? null);
  const [raison, setRaison] = useState('');
  const [documentId, setDocumentId] = useState(origine.piece?.documentId);
  const [erreur, setErreur] = useState('');
  const deposer = useMutationRegistre(galerieSlug, (fichier: File, appel) =>
    registrePoliceApi.deposerPiece(galerieSlug, fichier, appel),
  );
  const label = (cle: string) => t(`regie:registrePolice.${cle}`);
  const objet = {
    ...origine.objet,
    ...Object.fromEntries(champsObjet.map((cle) => [cle, valeurs[`objet.${cle}`]])),
  };
  const vendeur = {
    ...origine.vendeur,
    qualite,
    ...Object.fromEntries(
      champsVendeur
        .filter((cle) => cle !== 'siren' || valeurs['vendeur.siren'])
        .map((cle) => [cle, valeurs[`vendeur.${cle}`]]),
    ),
    adresse: Object.fromEntries(
      champsAdresse
        .filter((cle) => cle !== 'pays' || valeurs['vendeur.adresse.pays'])
        .map((cle) => [cle, valeurs[`vendeur.adresse.${cle}`]]),
    ),
  };
  const piece =
    voitPiece && qualite === 'particulier'
      ? {
          nature: valeurs['piece.nature'],
          numero: valeurs['piece.numero'],
          autorite: valeurs['piece.autorite'],
          ...(valeurs['piece.delivreeLe']
            ? { delivreeLe: new Date(valeurs['piece.delivreeLe']) }
            : {}),
          ...(documentId ? { documentId } : {}),
        }
      : undefined;
  const brouillon: BrouillonRegistrePolice = {
    modeAcquisition: mode,
    ...(valeurs['dateAcquisition']
      ? { dateAcquisition: new Date(valeurs['dateAcquisition']) }
      : {}),
    objet,
    vendeur,
    devise,
    ...(reglement ? { modeReglement: reglement } : {}),
    ...(prix !== null ? { prixAchatCentimes: prix } : {}),
    ...(mode === 'depot' ? { prixAchatCentimes: null } : {}),
    ...(piece ? { piece } : {}),
  };
  const resultat = brouillonRegistrePoliceSchema.safeParse(brouillon);
  const manquantes = mentionsManquantes(brouillon);
  function champ(chemin: string, type = 'text') {
    const manque = manquantes.includes(chemin);
    const invalide =
      !resultat.success && resultat.error.issues.some((issue) => issue.path.join('.') === chemin);
    let erreurChamp;
    if (manque) erreurChamp = label('mentionManquante');
    else if (invalide) erreurChamp = label('mentionInvalide');
    return (
      <Field
        key={chemin}
        htmlFor={`mention-${chemin}`}
        label={label(`champs.${chemin}`)}
        error={erreurChamp}
      >
        <Input
          id={`mention-${chemin}`}
          type={type}
          value={valeurs[chemin] ?? ''}
          onChange={(event) => setValeurs({ ...valeurs, [chemin]: event.target.value })}
        />
      </Field>
    );
  }
  async function soumettre(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErreur('');
    if (!resultat.success) {
      setErreur(label('mentionsInvalides'));
      return;
    }
    const bouton = (event.nativeEvent as SubmitEvent).submitter;
    await onEnregistrer(
      resultat.data,
      raison,
      bouton instanceof HTMLButtonElement && bouton.value === 'valider',
    );
  }
  return (
    <form className={styles.formulaire} onSubmit={(event) => void soumettre(event)}>
      <div className={styles.grille}>
        <Field htmlFor="mention-mode" label={label('champs.modeAcquisition')}>
          <Select
            id="mention-mode"
            value={mode}
            onValueChange={(valeur) => setMode(valeur as typeof mode)}
            options={REGLES_REGISTRE_POLICE.modesAcquisition.valeur.map((valeur) => ({
              value: valeur,
              label: label(`valeurs.${valeur}`),
            }))}
          />
        </Field>
        {champ('dateAcquisition', 'date')}
        {mode !== 'depot' && (
          <Field
            htmlFor="mention-prix"
            label={label('champs.prixAchatCentimes')}
            error={manquantes.includes('prixAchatCentimes') ? label('mentionManquante') : undefined}
          >
            <PriceInput
              id="mention-prix"
              lang={i18n.language === 'en' ? 'en' : 'fr'}
              optional
              cents={prix}
              onCentsChange={setPrix}
              currency={devise}
            />
          </Field>
        )}
        <Field htmlFor="mention-devise" label={label('champs.devise')}>
          <Select
            id="mention-devise"
            value={devise}
            onValueChange={(valeur) => setDevise(valeur as typeof devise)}
            options={DEVISES.map((valeur) => ({ value: valeur, label: valeur }))}
          />
        </Field>
        {mode !== 'depot' && (
          <Field
            htmlFor="mention-reglement"
            label={label('champs.modeReglement')}
            error={manquantes.includes('modeReglement') ? label('mentionManquante') : undefined}
          >
            <Select
              id="mention-reglement"
              value={reglement}
              onValueChange={(valeur) => setReglement(valeur as typeof reglement)}
              options={[
                { value: '', label: '—' },
                ...MODES_REGLEMENT.map((valeur) => ({
                  value: valeur,
                  label: label(`valeurs.${valeur}`),
                })),
              ]}
            />
          </Field>
        )}
      </div>
      <fieldset>
        <legend>{label('objet')}</legend>
        <div className={styles.grille}>{champsObjet.map((cle) => champ(`objet.${cle}`))}</div>
      </fieldset>
      <fieldset>
        <legend>{label('vendeur')}</legend>
        <div className={styles.grille}>
          <Field htmlFor="mention-qualite" label={label('champs.vendeur.qualite')}>
            <Select
              id="mention-qualite"
              value={qualite}
              onValueChange={(valeur) => setQualite(valeur as typeof qualite)}
              options={['particulier', 'professionnel'].map((valeur) => ({
                value: valeur,
                label: label(`valeurs.${valeur}`),
              }))}
            />
          </Field>
          {champsVendeur
            .filter((cle) =>
              qualite === 'particulier'
                ? cle === 'nom' || cle === 'prenom'
                : cle === 'raisonSociale' || cle === 'siren',
            )
            .map((cle) => champ(`vendeur.${cle}`))}
          {champsAdresse.map((cle) => champ(`vendeur.adresse.${cle}`))}
        </div>
      </fieldset>
      {voitPiece && qualite === 'particulier' && (
        <fieldset>
          <legend>{label('piece')}</legend>
          <div className={styles.grille}>
            {champsPiece.map((cle) =>
              champ(`piece.${cle}`, cle === 'delivreeLe' ? 'date' : 'text'),
            )}
            <Field htmlFor="mention-scan" label={label('scan')}>
              <Input
                id="mention-scan"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                disabled={enCours || deposer.isPending}
                onChange={(event) => {
                  const fichier = event.target.files?.[0];
                  if (!fichier) return;
                  void deposer
                    .mutateAsync(fichier)
                    .then((reponse) => setDocumentId(reponse.documentId))
                    .catch(() => undefined);
                }}
              />
            </Field>
            {documentId && <p>{label('scanDepose')}</p>}
            {deposer.isError && (
              <p role="alert">
                {estApiError(deposer.error) ? deposer.error.message : t('commun:erreurReseau')}
              </p>
            )}
          </div>
        </fieldset>
      )}
      {raisonRequise && (
        <Field htmlFor="mention-raison" label={label('raison')}>
          <Textarea
            id="mention-raison"
            required
            value={raison}
            onChange={(event) => setRaison(event.target.value)}
          />
        </Field>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      <div className={styles.actions}>
        <Button
          type="submit"
          disabled={enCours || deposer.isPending || (raisonRequise && !raison.trim())}
        >
          {label(raisonRequise ? 'rectifier' : 'enregistrerMentions')}
        </Button>
        {peutValider && (
          <Button type="submit" value="valider" disabled={enCours || deposer.isPending}>
            {label('valider')}
          </Button>
        )}
      </div>
    </form>
  );
}
